import { useEffect, useMemo } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import FormHelperText from '@material-ui/core/FormHelperText';
import useAsync from 'react-use/lib/useAsync';

import { migrationApiRef, type ServiceRow } from '../../migration/api';
import { statusFilterLabel, statusLabel } from '../../migration/common';
import { filterServicesByType } from '../../migration/serviceTypes';

/**
 * Multi-select of one team's services, loaded from the migration database
 * via the migration backend — the same source the GitHub Migration
 * tab uses, so the wizard and the tab never disagree about what a team owns.
 *
 * The field is scoped by another field on the form (the team picker), in the
 * `dependsOn` idiom of DependentEntityPicker: no team, no list.
 */
const MigrationServicePickerFieldSchema = makeFieldSchema({
  output: z => z.array(z.string()),
  uiOptions: z =>
    z.object({
      serviceTypeFilter: z
        .enum(['lambda', 'non-lambda'])
        .optional()
        .describe(
          'Restrict services by their migration database type; omitted includes all supported types.',
        ),
      dependsOn: z
        .string()
        .optional()
        .describe(
          'The field name in the form data holding the team, defaults to "team". Its value may be an entity ref (`group:nte/nte`) or a bare team name.',
        ),
      statusFilter: z
        .string()
        .optional()
        .describe(
          'Only list services at one migration status or a comma-separated list of exact statuses, e.g. "step1-completed,step2-failed". Empty means no filter.',
        ),
    }),
});

export const MigrationServicePickerSchema = MigrationServicePickerFieldSchema;

type Props = typeof MigrationServicePickerFieldSchema.TProps;

/**
 * Team name out of the `dependsOn` field's value.
 *
 * The value is normally an entity ref (`kind:namespace/name`), and a team's
 * root group is `group:<team>/<team>`, so the team is the namespace segment —
 * the same segment DependentEntityPicker pulls out to scope its query. A bare
 * team name is accepted too, so the field also works behind a plain string.
 */
/**
 * The form's array value, with RJSF's placeholder entries removed.
 *
 * Every field these widgets back declares `minItems: 1`, and RJSF's
 * getDefaultFormState pre-populates an array up to minItems with a default
 * entry -- `undefined` for `items: {type: string}`, `{}` for an object. So the
 * form arrives holding one empty slot before the operator has chosen anything,
 * which read as a selection: the onboard picker announced "1 of 371 team(s)
 * selected" with no team shown, because the single entry had nothing to render.
 *
 * Filtering here rather than at each call site keeps the three widgets honest
 * about the same thing, and `useCleanedArray` below also writes the cleaned
 * value back so the placeholder is not submitted.
 */
export function cleanArray<T>(value: unknown): T[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    v =>
      v !== undefined &&
      v !== null &&
      v !== '' &&
      !(typeof v === 'object' && Object.keys(v as object).length === 0),
  ) as T[];
}

export function teamNameFromRef(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const [head, ...rest] = trimmed.split('/');
  const beforeSlash = head.includes(':') ? head.split(':')[1] : head;
  // `kind:namespace/name` -> namespace; `kind:name` or `name` -> name.
  return (rest.length > 0 ? beforeSlash || rest.join('/') : beforeSlash)
    .trim()
    .toLowerCase();
}

/** `nte-cluster1, nte-cluster2` or an em dash when the record has none. */
export function clusterSummary(service: ServiceRow): string {
  return service.clusters.length ? service.clusters.join(', ') : '—';
}

export const MigrationServicePicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
  uiSchema,
  idSchema,
  schema,
}: Props) => {
  const migrationApi = useApi(migrationApiRef);

  const options = uiSchema?.['ui:options'] ?? {};
  const dependsOnField = options.dependsOn ?? 'team';
  const statusFilter = (options.statusFilter ?? '').trim();
  const serviceTypeFilter = options.serviceTypeFilter;

  const team = teamNameFromRef(
    (formContext?.formData as Record<string, unknown> | undefined)?.[
      dependsOnField
    ],
  );

  const { value, loading, error } = useAsync(async () => {
    if (!team) return undefined;
    return await migrationApi.listServices(
      team,
      statusFilter ? statusFilter : undefined,
      serviceTypeFilter,
    );
  }, [migrationApi, team, statusFilter, serviceTypeFilter]);

  const selected = useMemo(() => cleanArray<string>(formData), [formData]);
  // RJSF seeds the array to `minItems` with a placeholder before the operator
  // picks anything. Hiding it is not enough -- an untouched form would submit
  // that placeholder to the workflow -- so the cleaned value is written back
  // once. Guarded on length, so this cannot loop.
  useEffect(() => {
    if (Array.isArray(formData) && formData.length !== selected.length) {
      onChange(selected);
    }
  }, [formData, selected, onChange]);

  const services = useMemo(
    () => filterServicesByType(value?.services ?? [], serviceTypeFilter),
    [value, serviceTypeFilter],
  );

  // Drop selections the current team does not own. This is what clears the
  // field when the team changes: the new team's list never contains the old
  // team's services. Pruning is skipped while the request is in flight, and
  // when it failed, so a transient error does not silently wipe the form.
  useEffect(() => {
    if (loading || selected.length === 0) return;
    if (!team) {
      onChange([]);
      return;
    }
    if (error || !value) return;
    const available = new Set(services.map(s => s.name));
    const kept = selected.filter(name => available.has(name));
    if (kept.length !== selected.length) {
      onChange(kept);
    }
  }, [loading, error, value, team, services, selected, onChange]);

  const secondaryByName = useMemo(
    () =>
      new Map(
        services.map(sv => [
          sv.name,
          `${clusterSummary(sv)} \u00b7 ${statusLabel(sv.status)}`,
        ]),
      ),
    [services],
  );

  const helperText = () => {
    if (!team) return `Select a team in "${dependsOnField}" first.`;
    if (loading) return `Loading ${team}'s services…`;
    // The backend distinguishes "you are not a member of this team" from a
    // lookup failure; show its wording rather than an empty dropdown.
    if (error) return `Could not load services: ${error.message}`;
    if (services.length === 0) {
      return statusFilter
        ? `No services of '${team}' match the allowed migration statuses: ${statusFilterLabel(statusFilter)}.`
        : `No services found for '${team}' in the migration database.`;
    }
    return `${selected.length} of ${services.length} service(s) selected`;
  };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && selected.length === 0}
      fullWidth
    >
      <Autocomplete
        multiple
        disableCloseOnSelect
        id={idSchema?.$id}
        options={services.map(sv => sv.name)}
        value={selected}
        disabled={!team || loading || Boolean(error)}
        onChange={(_e, v) => onChange(v as string[])}
        getOptionLabel={name => name}
        renderOption={(name, { selected: isSel }) => (
          <>
            <Checkbox checked={isSel} style={{ marginRight: 8 }} />
            <span>
              <span style={{ display: 'block' }}>{name}</span>
              <span
                style={{ display: 'block', opacity: 0.7, fontSize: '0.8em' }}
              >
                {secondaryByName.get(name)}
              </span>
            </span>
          </>
        )}
        renderTags={(v, getTagProps) =>
          v.map((name, index) => (
            <Chip
              {...getTagProps({ index })}
              key={name}
              label={name}
              size="small"
            />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            label={schema.title ?? 'Services'}
            placeholder={
              selected.length ? undefined : 'Type to filter services…'
            }
          />
        )}
      />
      <Box mt={1}>
        <Button
          size="small"
          disabled={
            !team ||
            loading ||
            Boolean(error) ||
            services.length === 0 ||
            selected.length === services.length
          }
          onClick={() => onChange(services.map(sv => sv.name))}
        >
          Select all ({services.length})
        </Button>
        <Button
          size="small"
          disabled={selected.length === 0}
          onClick={() => onChange([])}
        >
          Clear
        </Button>
      </Box>
      <FormHelperText>{helperText()}</FormHelperText>
      {schema.description && (
        <FormHelperText id={`${idSchema?.$id}-description`}>
          {schema.description}
        </FormHelperText>
      )}
    </FormControl>
  );
};
